import type { BoardGeometry, BoardPlayer, Cell, PlayerColor, Rect } from '@/shared/board/types'

/**
 * Вид игрового поля: во что укладываются те же самые 96 клеток кольца.
 *
 * Правила координат не знают. Кольцо остаётся длиной 96, старты на
 * 0/24/48/72, входы в дом на 94/22/46/70, углы прыжка на 10/34/58/82 —
 * меняется только то, какая клетка стоит под каким номером. Поэтому смена
 * вида не задевает ни движок (`features/engine`), ни бэкенд, ни золотой
 * корпус партий: они работают с номерами.
 *
 * Зачем второй вид. Крест — не выбор оформления, а следствие длины кольца:
 * квартал считается как `2·armLength + armWidth − 1 = 24`, а сторона доски
 * как `2·armLength + armWidth = 25`. У любого выпуклого контура выходит то
 * же самое (`4W − 4 = 96` даёт `W = 25`). Пока путь идёт по краю, сторона
 * равна 25 при любой форме, и на телефоне клетка остаётся 14,4 пикселя.
 *
 * Сжать доску можно единственным способом — увести путь внутрь. Каждая
 * сторона получает складку: путь сходит с края, идёт вглубь на DEPTH клеток,
 * разворачивается и возвращается. Складка добавляет `2·DEPTH` клеток
 * кварталу и на столько же укорачивает сторону, то есть
 * `size = quarter + 1 − 2·DEPTH`: 17 клеток вместо 25 и 21,2 пикселя вместо
 * 14,4.
 *
 * Глубже четырёх складка не идёт: домашняя дорожка — пять клеток от входа
 * внутрь, и при DEPTH = 5 складка соседней стороны встаёт ровно на её пятую
 * клетку. Проверяет это `layout.test.ts` — там же и доказательство, что
 * раскладка вообще собирается: геометрия считается формулами и от данных
 * не зависит, так что теста для этого достаточно.
 */
export type BoardKind = 'classic' | 'meander'

/** Насколько путь уходит внутрь на каждой стороне. Больше четырёх не бывает. */
const DEPTH = 4

/** Поворот клетки на 90°·turn против часовой — как в engine/board.ts. */
function rotate(cell: Cell, turn: number, size: number): Cell {
  let row = cell.row
  let col = cell.col

  for (let i = 0; i < turn; i++) {
    const turned = size - 1 - col
    col = row
    row = turned
  }

  return { row, col }
}

/** Поворот прямоугольника: стороны при повороте на 90° меняются местами. */
function rotateRect(rect: Rect, turn: number, size: number): Rect {
  const first = rotate({ row: rect.row, col: rect.col }, turn, size)
  const second = rotate(
    { row: rect.row + rect.height - 1, col: rect.col + rect.width - 1 },
    turn,
    size,
  )

  return {
    row: Math.min(first.row, second.row),
    col: Math.min(first.col, second.col),
    height: Math.abs(first.row - second.row) + 1,
    width: Math.abs(first.col - second.col) + 1,
  }
}

/**
 * Четверть кольца: край, складка внутрь, снова край.
 *
 * Идёт сверху вниз по левому краю — в ту же сторону, что и в кресте, против
 * часовой стрелки. `knee` — клетка, на которой путь сходит с края; она
 * считается из номера угла прыжка, потому что вершина складки обязана
 * оказаться под этим номером.
 */
function quarterCells(size: number, knee: number, depth: number): Cell[] {
  const cells: Cell[] = []

  for (let row = 0; row <= knee; row++) cells.push({ row, col: 0 })
  for (let col = 1; col <= depth; col++) cells.push({ row: knee, col })

  cells.push({ row: knee + 1, col: depth })
  cells.push({ row: knee + 2, col: depth })

  for (let col = depth - 1; col >= 0; col--) cells.push({ row: knee + 2, col })
  for (let row = knee + 3; row < size - 1; row++) cells.push({ row, col: 0 })

  return cells
}

/**
 * Места фишек во дворе — «пятёркой», как точки на кубике.
 *
 * Рядом из пяти они не встают: двор компактной раскладки шириной пять клеток,
 * и ряд занял бы его целиком, не оставив места рамке. А главное — пятёрка
 * совпадает сама с собой при повороте на 90°, значит все четыре двора
 * выглядят одинаково при любом развороте доски, чего у ряда нет: он встаёт
 * то строкой, то столбиком. Крест раскладывает двор так же (`baseSlots`
 * в `features/engine/board.ts` и `Board::baseSlots()` на бэкенде).
 *
 * Углы — на соседних клетках от середины при любом размере двора: фишка стоит
 * на клетке, и лишняя клетка между ними разбивала бы фигуру на пять отдельных
 * кружков. Двор у креста вдвое шире кармана меандра, но фигура в обоих одна
 * и та же — она про пять фишек, а не про размер двора.
 *
 * Для любого другого числа фишек — ряд по центру: пятёрка рисуется только
 * для пяти, а число фишек задаётся правилами.
 */
function yardSlots(tray: Rect, tokens: number): Cell[] {
  const row = tray.row + Math.floor((tray.height - 1) / 2)
  const col = tray.col + Math.floor((tray.width - 1) / 2)
  const step = 1

  if (tokens === 5) {
    return [
      { row: row - step, col: col - step },
      { row: row - step, col: col + step },
      { row, col },
      { row: row + step, col: col - step },
      { row: row + step, col: col + step },
    ]
  }

  const from = tray.col + Math.floor((tray.width - tokens) / 2)

  return Array.from({ length: tokens }, (_, index) => ({ row, col: from + index }))
}

/**
 * Меандр: те же клетки, уложенные вчетверо компактнее.
 *
 * Всё, что описывает правила — длина кольца, номера стартов, входов и углов,
 * длина дома, число фишек, — берётся из пришедшей доски, а не пишется здесь
 * заново. Поэтому раскладка не может разойтись с правилами молча: изменится
 * геометрия в движке — изменятся и выведенные из неё числа.
 */
function meander(board: BoardGeometry): BoardGeometry {
  const quarter = board.ringLength / board.players.length
  // Номер угла прыжка внутри квартала: под ним обязана оказаться вершина
  // складки. Отсюда и глубина, на которую путь сходит с края.
  const cornerAt = board.innerCorners[0].index % quarter
  // Насколько вход в дом отстоит назад от своего старта. Он же — колонка,
  // по которой уходит внутрь домашняя дорожка.
  const startToEntry =
    (board.players[0].startIndex - board.players[0].entryIndex + board.ringLength) %
    board.ringLength
  const size = quarter + 1 - 2 * DEPTH
  const knee = cornerAt - DEPTH

  const cells = quarterCells(size, knee, DEPTH)
  const ring = [0, 1, 2, 3].flatMap((turn) => cells.map((cell) => rotate(cell, turn, size)))

  // Дом красного: от входа внутрь по колонке startToEntry. У остальных —
  // поворотом, как и всё прочее.
  const home = Array.from({ length: board.homeLength }, (_, index) => ({
    row: index + 1,
    col: startToEntry,
  }))
  // Двор — свободный карман у своего угла: от домашней дорожки до складки
  // соседней стороны. Квадратный, поэтому при повороте не меняет форму.
  const tray: Rect = {
    row: 1,
    col: startToEntry + 1,
    height: knee - 1,
    width: knee - 1,
  }
  const slots = yardSlots(tray, board.tokensPerPlayer)

  const players: BoardPlayer[] = board.players.map((player) => {
    const turn = player.startIndex / quarter

    return {
      ...player,
      home: home.map((cell) => rotate(cell, turn, size)),
      base: {
        rect: rotateRect(tray, turn, size),
        slots: slots.map((cell) => rotate(cell, turn, size)),
      },
    }
  })

  return {
    ...board,
    size,
    ring,
    players,
    /*
     * Середина, свободная и от складок, и от дворов.
     *
     * Отступ DEPTH + 2, а не DEPTH + 1: от складок хватило бы и первого,
     * но двор занимает строки 1..knee−1, и его последняя строка попадала
     * ровно на первую строку середины. Рисуется середина после дворов
     * и накрывала им угол.
     */
    center: {
      rect: {
        row: DEPTH + 2,
        col: DEPTH + 2,
        height: size - 2 * (DEPTH + 2),
        width: size - 2 * (DEPTH + 2),
      },
    },
    innerCorners: board.innerCorners.map(({ index }) => ({ index, cell: ring[index] })),
    // Неигровых клеток внутри контура у меандра нет: рисовать нечего.
    deadCells: [],
  }
}

/**
 * Классика: свой луч — справа от своего двора, а не над ним.
 *
 * В геометрии движка двор стоит в углу, а луч с его стартом и домом — над
 * ним. Развёрнутая к игроку (`faceTo`), доска клала свой дом слева, вертикально,
 * и старт с домом приходилось искать глазами над двором. Справа, по нижнему
 * краю, они лежат там, куда уходят фишки из-под большого пальца.
 *
 * Поворачиваются ЛУЧИ — кольцо, дома, углы прыжка — на четверть против
 * часовой, а дворы остаются в своих углах. Всю доску повернуть нельзя: двор
 * повернулся бы вместе со своим лучом, и всё осталось бы как было. Зеркалить
 * тоже нельзя: зеркало развернуло бы ход на «по часовой», а он по правилам
 * против. Поворот направление хода сохраняет.
 *
 * Номера клеток не меняются — ни движок, ни сервер, ни корпус партий об этом
 * не знают. Середина и неигровые клетки симметричны, поворот их не меняет.
 */
function armsBesideYards(board: BoardGeometry): BoardGeometry {
  const turned = turnBoard(board, 1)

  return {
    ...turned,
    players: turned.players.map((player, index) => ({
      ...player,
      base: board.players[index].base,
    })),
    center: board.center,
  }
}

/**
 * Доска в выбранном виде.
 *
 * Классика — геометрия движка с лучами, повёрнутыми к дворам
 * (`armsBesideYards`). Если выведенные для меандра числа окажутся
 * невозможными (изменились правила, и складка перестала помещаться),
 * возвращается классика: доска без вида играется, доска с битой геометрией —
 * нет. Что этой ветки сейчас не происходит, проверяет `layout.test.ts`.
 */
export function relayout(board: BoardGeometry, kind: BoardKind): BoardGeometry {
  if (kind === 'classic') return armsBesideYards(board)

  const quarter = board.ringLength / board.players.length
  const size = quarter + 1 - 2 * DEPTH
  const knee = (board.innerCorners[0].index % quarter) - DEPTH

  // Складка обязана оставить место и двору (его сторона — knee − 1 клетка),
  // и середине под кубики.
  if (knee < 3 || size - 2 * (DEPTH + 2) < 1) return armsBesideYards(board)

  return meander(board)
}

/** Повернуть все координаты доски на 90°·turns, не трогая номера клеток. */
function turnBoard(board: BoardGeometry, turns: number): BoardGeometry {
  if (turns % 4 === 0) return board

  const cell = (one: Cell) => rotate(one, turns, board.size)

  return {
    ...board,
    ring: board.ring.map(cell),
    players: board.players.map((player) => ({
      ...player,
      home: player.home.map(cell),
      base: {
        rect: rotateRect(player.base.rect, turns, board.size),
        slots: player.base.slots.map(cell),
      },
    })),
    center: { rect: rotateRect(board.center.rect, turns, board.size) },
    innerCorners: board.innerCorners.map((corner) => ({ ...corner, cell: cell(corner.cell) })),
    deadCells: board.deadCells.map(cell),
  }
}

/**
 * Доска, развёрнутая своим двором к игроку — вниз и влево.
 *
 * Играть сверху вниз не то же самое, что снизу вверх: свои фишки должны быть
 * под большим пальцем, а свой дом — вести от себя. Цвета раздаются при старте,
 * и свой может достаться любому углу, поэтому доворачивает клиент.
 *
 * Поворачиваются КООРДИНАТЫ, а не картинка. Номера клеток остаются на месте,
 * то есть правила о развороте не знают; а `transform` на группе развернул бы
 * вместе с доской имена в карточках, счёт и кубики, и их пришлось бы
 * контр-поворачивать поштучно.
 *
 * Снизу слева, а не снизу справа: правый нижний угол на телефоне перекрывает
 * большой палец правой руки — там же, где кнопка «вся доска».
 */
export function faceTo(board: BoardGeometry, colour: PlayerColor | null): BoardGeometry {
  if (colour === null) return board

  const player = board.players.find((one) => one.color === colour)

  if (player === undefined) return board

  const half = board.size / 2

  for (let turns = 0; turns < 4; turns++) {
    const rect = rotateRect(player.base.rect, turns, board.size)

    if (rect.row + rect.height > half && rect.col < half) return turnBoard(board, turns)
  }

  return board
}
